// FILENAME: frontend/src/pages/wip/WipDetailPaneWireframePage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  enrollmentStates,
  mockSummary,
  mockTimeline,
  statusCards,
} from './detailPaneWireframe/mockData.js';
import { DetailPaneStatusCard, DetailPaneStatusFlipCard } from './detailPaneWireframe/DetailPaneWireframeCards.jsx';

const DetailPaneWireframeSection = ({ title, caption, children }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: '1px solid rgba(15, 24, 35, 0.08)',
        background: 'rgba(255, 255, 255, 0.72)',
        boxShadow: '0 22px 50px rgba(30, 42, 68, 0.08)',
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#101828' }}>
            {title}
          </Typography>
          {caption ? (
            <Typography sx={{ color: 'rgba(16, 24, 40, 0.6)', mt: 0.5 }}>{caption}</Typography>
          ) : null}
        </Box>
        {children}
      </Stack>
    </Paper>
  );
};

const WipDetailPaneWireframePage = () => {
  const [activeEnrollmentState, setActiveEnrollmentState] = useState(enrollmentStates[0].id);
  const currentEnrollmentState = useMemo(
    () => enrollmentStates.find((state) => state.id === activeEnrollmentState) || enrollmentStates[0],
    [activeEnrollmentState],
  );

  useEffect(() => {
    const fontLinks = [
      {
        id: 'wip-detailpane-fonts-jakarta',
        href: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap',
      },
      {
        id: 'wip-detailpane-fonts-fraunces',
        href: 'https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&display=swap',
      },
    ];

    fontLinks.forEach((font) => {
      if (document.getElementById(font.id)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = font.href;
      link.id = font.id;
      document.head.appendChild(link);
    });

    return () => {
      fontLinks.forEach((font) => {
        const link = document.getElementById(font.id);
        if (link) link.remove();
      });
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 4 },
        background: 'radial-gradient(circle at top left, #f7f2ea 0%, #eef1f6 35%, #e2e8f0 100%)',
        fontFamily: '"Plus Jakarta Sans", "Segoe UI", sans-serif',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack spacing={3}>
          <Box>
            <Chip label="WIP" color="warning" size="small" sx={{ mb: 1 }} />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontFamily: '"Fraunces", "Times New Roman", serif',
                color: '#0f172a',
              }}
            >
              Detail Pane Wireframe
            </Typography>
            <Typography sx={{ color: 'rgba(15, 23, 42, 0.7)', mt: 1 }}>
              Slimmed-down status cards with clearer hierarchy, built for quick iteration.
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              border: '1px solid rgba(15, 24, 35, 0.1)',
              background: 'rgba(255, 255, 255, 0.78)',
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: '#101828', fontFamily: '"Fraunces", serif' }}
                  >
                    Canyon Ridge Auto - BAC 302-884-191
                  </Typography>
                  <Typography sx={{ color: 'rgba(16, 24, 40, 0.6)' }}>
                    Program snapshot for GMX Enrollment - Updated 2 hours ago
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained">Refresh data</Button>
                  <Button variant="outlined">Open dealer</Button>
                </Stack>
              </Stack>

              <Divider />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
                <Box sx={{ flex: 1.35, minWidth: 0 }}>
                  <DetailPaneWireframeSection
                    title="Status Details"
                    caption="Primary indicators and date markers for enrollment health."
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, minmax(0, 1fr))',
                          lg: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 1.5,
                      }}
                    >
                      <DetailPaneStatusFlipCard
                        label="Enrollment Status"
                        statusLabel={currentEnrollmentState.chip}
                        dateLabel={currentEnrollmentState.dateLabel}
                        dateValue={currentEnrollmentState.dateValue}
                        tone={currentEnrollmentState.tone}
                      />
                      {statusCards.map((card) => (
                        <DetailPaneStatusCard key={card.label} {...card} />
                      ))}
                    </Box>
                  </DetailPaneWireframeSection>
                </Box>
                <Stack spacing={2} sx={{ flex: 0.85, minWidth: 0 }}>
                  <DetailPaneWireframeSection
                    title="Dealer Summary"
                    caption="At-a-glance context for the enrollment conversation."
                  >
                    <Stack spacing={1.25}>
                      {mockSummary.map((item) => (
                        <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <Typography sx={{ color: 'rgba(16, 24, 40, 0.6)' }}>{item.label}</Typography>
                          <Typography sx={{ fontWeight: 600, color: '#101828' }}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </DetailPaneWireframeSection>

                  <DetailPaneWireframeSection
                    title="Key Events"
                    caption="Recent changes that explain the current status mix."
                  >
                    <Stack spacing={1.25}>
                      {mockTimeline.map((event, index) => (
                        <Box key={event.label}>
                          <Typography sx={{ fontWeight: 600, color: '#101828' }}>{event.label}</Typography>
                          <Typography sx={{ color: 'rgba(16, 24, 40, 0.6)', fontSize: 14 }}>{event.value}</Typography>
                          {index < mockTimeline.length - 1 ? (
                            <Divider sx={{ my: 1.25 }} />
                          ) : null}
                        </Box>
                      ))}
                    </Stack>
                  </DetailPaneWireframeSection>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          px: 1.5,
          py: 0.75,
          borderRadius: 999,
          border: '1px solid rgba(15, 24, 35, 0.12)',
          background: 'rgba(15, 23, 42, 0.9)',
          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.22)',
          zIndex: 10,
        }}
      >
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          {enrollmentStates.map((state) => {
            const isActive = state.id === activeEnrollmentState;
            return (
              <Button
                key={state.id}
                size="small"
                variant={isActive ? 'contained' : 'text'}
                onClick={() => setActiveEnrollmentState(state.id)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 32,
                  px: 1.5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: isActive ? '#0f172a' : 'rgba(255, 255, 255, 0.75)',
                  background: isActive ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                  '&:hover': {
                    background: isActive ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.12)',
                  },
                }}
              >
                {state.chip}
              </Button>
            );
          })}
        </Stack>
      </Paper>
    </Box>
  );
};

export default WipDetailPaneWireframePage;
